import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { ApiError } from '@/api/client';
import ErrorBanner from '@/components/ErrorBanner';
import { GITHUB_APP_INSTALL_URL } from '@/lib/config';

export default function LoginPage() {
  const { isAuthenticated, isLoading, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fromState = (location.state as { from?: { pathname: string; search: string } } | null)
    ?.from;

  useEffect(() => {
    if (isAuthenticated) {
      const sessionTarget = sessionStorage.getItem('postLoginReturnTo');
      sessionStorage.removeItem('postLoginReturnTo');
      const target =
        sessionTarget ??
        (fromState ? `${fromState.pathname}${fromState.search ?? ''}` : '/dashboard');
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, fromState, navigate]);

  if (isLoading) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(new Error('Invalid email or password.'));
      } else {
        setError(err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="centered-full login-card">
      <div className="login-inner">
        <h1>InsightPM</h1>
        <p className="muted">Sign in to manage PR analytics for your team.</p>
        <form className="form-grid" onSubmit={handleSubmit}>
          {error ? <ErrorBanner error={error} /> : null}
          <label>
            <span>Email</span>
            <input
              className="input"
              type="email"
              autoComplete="off"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            <span>Password</span>
            <input
              className="input"
              type="password"
              autoComplete="off"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !email || !password}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <div className="login-links">
          <Link to="/forgot-password">Forgot password?</Link>
          <span className="muted">
            Need an account?{' '}
            <a
              href={GITHUB_APP_INSTALL_URL}
              target="_blank"
              rel="noreferrer"
            >
              Install the GitHub App
            </a>{' '}
            to get started, or request an invite from your organization owner.
          </span>
          <span className="muted">
            Just looking around? <Link to="/demo">Try the interactive demo</Link> — sample
            data, no signup.
          </span>
        </div>
      </div>
    </div>
  );
}
